<template>
  <div
    v-if="showFloatingComments && loadingView"
    :class="['loading-comments-view', 'comments-container']"
  ></div>
  <FloatingComments
    v-else-if="showFloatingComments && !loadingView"
    class="comments-container"
    :comments="floatingComments"
    :current-user="currentUser"
    :selected-id="selectedCommentId"
    :participants="participants"
    :sender="sender"
    :dimensions-updating="dimensionsUpdating"
    :prevent-comments-modification="false"
    :mentions-enabled="true"
    @update="contextualCommentingData.updateComment"
    @create-reply="handleCreateReply"
    @select="$event => contextualCommentingData.selectComment($event, participant.id)"
    @delete="contextualCommentingData.deleteComment"
  />
  <CommentCarousel
    v-else-if="isCommentCarouselOpen"
    :comments="floatingComments"
    :current-user="currentUser"
    :sender="sender"
    :dimensions-updating="dimensionsUpdating"
    :selected-id="selectedCommentId"
    :participants="participants"
    :mentions-enabled="true"
    @close="handleCloseCommentCarousel"
    @view-all="handleViewAllComments"
    @update="contextualCommentingData.updateComment"
    @create-reply="handleCreateReply"
    @select="$event => contextualCommentingData.selectComment($event, participant.id)"
    @delete="contextualCommentingData.deleteComment"
  />
</template>
<script lang="ts">
import type { Page, Participant } from '@getaccept/dsr-shared-new';
import FloatingComments from '@getaccept/dsr-shared-new/src/contextual-commenting/components/FloatingComments.vue';
import { FloatingCommentsHelper } from '@getaccept/dsr-shared-new/src/contextual-commenting/helpers/floating-comments.helper';
import { useContextualCommentingStore } from '@getaccept/dsr-shared-new/src/contextual-commenting/store/contextual-commenting.store';
import type { Comment } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/comment';
import type { CreateReplyInput } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/create-reply-input';
import type { CurrentUser } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/current-user';
import type { ComputedRef, PropType, Ref } from 'vue';
import { computed, defineComponent, ref, toRefs, watch } from 'vue';
import { AvatarHelper } from '@getaccept/lib-shared-new/src/helpers/avatars.helper';
import CommentCarousel from '@getaccept/dsr-shared-new/src/contextual-commenting/comments-carousel/components/CommentCarousel.vue';
import { useContextualCommentingData } from '../composables/contextual-commenting-data.composable';

export default defineComponent({
  components: {
    FloatingComments,
    CommentCarousel,
  },
  props: {
    participants: {
      type: Array as PropType<Participant[]>,
      required: true,
    },
    participant: {
      type: Object as PropType<Participant>,
      required: true,
    },
    currentPage: {
      type: Object as PropType<Page>,
      required: true,
    },
    sender: {
      type: Object as PropType<Participant>,
      required: true,
    },
    dimensionsUpdating: {
      type: Boolean,
    },
    showFloatingComments: { type: Boolean },
    loadingView: { type: Boolean },
  },
  setup(props, { emit }) {
    const {
      participant,
      currentPage,
    }: { participant: Ref<Participant>; currentPage: Ref<Page> } = toRefs(props);
    const contextualCommentingStore = useContextualCommentingStore();
    const contextualCommentingData = useContextualCommentingData();

    const comments = computed((): Comment[] => contextualCommentingStore.viewableComments);

    const floatingComments = computed((): Comment[] => {
      const { editorBlock } = currentPage.value;
      const filteredComments = comments.value.filter(
        (comment: Comment) => comment.editorSelection.editorBlockId === editorBlock.id
      );
      return FloatingCommentsHelper.getComments(filteredComments, [editorBlock]);
    });

    const showComments = computed(() => !!floatingComments.value.length);

    const currentUser: ComputedRef<CurrentUser> = computed(() => ({
      participantId: participant.value.id,
      name: AvatarHelper.getDisplayName(participant.value),
      thumbUrl: participant.value.thumbUrl,
    }));

    const selectedCommentId = computed((): string => contextualCommentingStore.selectedCommentId);

    const handleCreateReply = (payload: CreateReplyInput) => {
      contextualCommentingData.createReply(payload, props.participant.id);
    };

    const isCommentCarouselOpen = ref(false);

    const handleCloseCommentCarousel = () => {
      isCommentCarouselOpen.value = false;
      contextualCommentingData.selectComment('', props.participant.id);
    };

    const handleViewAllComments = () => {
      isCommentCarouselOpen.value = false;
      contextualCommentingStore.toggleCommentHistory(true);
    };

    watch(
      () => contextualCommentingStore.selectedCommentId,
      (value: string) => {
        if (!value) {
          return;
        }
        isCommentCarouselOpen.value = true;
        if (!props.showFloatingComments) {
          emit('scroll-to-comment', value);
        }
      }
    );

    return {
      contextualCommentingData,
      selectedCommentId,
      showComments,
      floatingComments,
      currentUser,
      isCommentCarouselOpen,
      handleCreateReply,
      handleCloseCommentCarousel,
      handleViewAllComments,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/contextual-commenting/scss/contextual-comments';

.comments-container {
  width: $comments-container-width;
  flex-shrink: 0;
}
</style>
